// @flow

import * as Immutable               from 'immutable';
import connect                      from 'react-redux/lib/connect/connect';
import * as Recompose               from 'recompose';
import displayCookieNotAllowedModal from '../../../../actions/cookieSettings/displayCookieNotAllowedModal';
import updateUserCookieSettings     from '../../../../actions/cookieSettings/updateUserCookieSettings';
import type {
  CookieType,
  CookieTypeConfig,
}                                   from '../../../../config/cookiesConfig';
import cookiesConfig                from '../../../../config/cookiesConfig';
import {
  selectIsCookieTypeEnabled,
  selectUserCookieSettings,
}                                   from '../../../../selectors/cookieSettingsSelectors';
import getCookieTypeConfigHash      from '../../../../utils/cookieSettings/getCookieTypeConfigHash';


// -----------------------------------------------------------------------------------------------------------------
// Main HOC
// -----------------------------------------------------------------------------------------------------------------

export type PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps> = {
  cookieSettings_requiredCookieTypes: Array<CookieType>,
  ...OtherProps,
};

export type PropsForEnhancedComponentWithoutRequiredCookieTypes<OtherProps> = {
  ...OtherProps,
};

export type PropsForEnhancedComponent<OtherProps> =
  | PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>
  | PropsForEnhancedComponentWithoutRequiredCookieTypes<OtherProps>;

export type PropsForBaseComponentWithRequiredCookieTypes<OtherProps> =
  | {
  ...$Diff<PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>, { cookieSettings_requiredCookieTypes: $PropertyType<PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>, 'cookieSettings_requiredCookieTypes'> }>,
  cookieSettings_haveUserSettings: boolean,
  cookieSettings_areUserSettingsCurrent: boolean,
  cookieSettings_areRequiredCookieTypesAllowed: true,
  cookieSettings_acceptAll: () => void,
}
  | {
  ...$Diff<PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>, { cookieSettings_requiredCookieTypes: $PropertyType<PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>, 'cookieSettings_requiredCookieTypes'> }>,
  cookieSettings_haveUserSettings: boolean,
  cookieSettings_areUserSettingsCurrent: boolean,
  cookieSettings_areRequiredCookieTypesAllowed: false,
  cookieSettings_acceptAll: () => void,
  cookieSettings_displayCookieNotAllowedModal: () => void,
};

export type PropsForBaseComponentWithoutRequiredCookieTypes<OtherProps> = {
  ...$Diff<PropsForEnhancedComponentWithoutRequiredCookieTypes<OtherProps>, { cookieSettings_requiredCookieTypes: $PropertyType<PropsForEnhancedComponentWithoutRequiredCookieTypes<OtherProps>, 'cookieSettings_requiredCookieTypes'> }>,
  cookieSettings_haveUserSettings: boolean,
  cookieSettings_areUserSettingsCurrent: boolean,
  cookieSettings_acceptAll: () => void,
};

type MapUserCookieSettings<OtherProps> =
  | Recompose.HOC<PropsForBaseComponentWithRequiredCookieTypes<OtherProps>, PropsForEnhancedComponentWithRequiredCookieTypes<OtherProps>>
  | Recompose.HOC<PropsForBaseComponentWithoutRequiredCookieTypes<OtherProps>, PropsForEnhancedComponentWithoutRequiredCookieTypes<OtherProps>>


/**
 * Adds props detailing whether user has allowed storing of cookies/localstorage data.
 *
 * Expects the following props for config, all prefixed `cookieSettings_`…
 *
 * - requiredCookieTypes: Array<CookieType> - Array of required cookie types.  If provided, will return whetehr the
 *                                            user  has allowed all of the required cookie types. Also, if the user has
 *                                            not granted permission to all the cookie types, then the
 *                                            `cookieSettings_displayCookieNotAllowedModal` prop will be provided to
 *                                            allow displaying of the 'cookies required' modal.
 *
 *
 * Will provide the following props, all prefixed `cookieSettings_`…
 *
 * - haveUserSettings: boolean                 - True if the user has saved any settings (but possibly not all the
 *                                               current ones).
 * - areUserSettingsCurrent: boolean           - True if the hash values for the users match the current cookie config
 *                                               hashes, and the user has saved preference for all the types of
 *                                               cookies.
 * - areRequiredCookieTypesAllowed?: boolean   - True if all of the `cookieSettings_requiredCookieTypes` types
 *                                               have been allowed by the user.
 *                                               This prop is only provided if the
 *                                               `cookieSettings_requiredCookieTypes` prop is set, and not empty.
 * - acceptAll: () => void                     - Callback to mark all cookies as allowed for the user.
 * - displayCookieNotAllowedModal?: () => void - Callback to display the 'cookies required' modal.
 *                                               This prop is only provided if the
 *                                               `cookieSettings_requiredCookieTypes` prop is set, and not empty, and
 *                                               the required cookies are not yet allowed.
 */
const mapUserCookieSettings: MapUserCookieSettings<*> =
        // $FlowFixMe
        Recompose.compose(
          Recompose.setDisplayName('mapUserCookieSettings'),
          connect(
            (state, {cookieSettings_requiredCookieTypes}: PropsForEnhancedComponent<*>) => {
              const userSettings           = selectUserCookieSettings(state);
              const areUserSettingsCurrent = Immutable.Map(cookiesConfig).reduce(
                (areUserSettingsCurrent: boolean,
                 cookieTypeConfig: CookieTypeConfig,
                 cookieType: CookieType): boolean => {
                  // If an entry has failed, no need to process other entries
                  if (!areUserSettingsCurrent) {
                    return areUserSettingsCurrent;
                  }

                  return getCookieTypeConfigHash(cookieType) === userSettings.getIn([cookieType, 'configHash'], null);
                },
                true,
              );

              let props: {} = {
                cookieSettings_haveUserSettings:       !userSettings.isEmpty(),
                cookieSettings_areUserSettingsCurrent: areUserSettingsCurrent,
              };

              // Add `areRequiredCookieTypesAllowed` if `cookieSettings_requiredCookieTypes` prop is provided
              if (cookieSettings_requiredCookieTypes) {
                props = {
                  ...props,
                  cookieSettings_areRequiredCookieTypesAllowed:
                    Immutable.List(cookieSettings_requiredCookieTypes).reduce(
                      (areRequiredCookieTypesAllowed: boolean, requiredCookieType: CookieType) => {
                        if (areRequiredCookieTypesAllowed) {
                          return selectIsCookieTypeEnabled(state, {cookieType: requiredCookieType});
                        }

                        return areRequiredCookieTypesAllowed;
                      },
                      true,
                    ),
                };
              }

              return props;
            },
            (dispatch, {cookieSettings_requiredCookieTypes}) => {
              const userSettings = Immutable.Map(cookiesConfig)
                .map((cookieTypeConfig: CookieTypeConfig, cookieType: CookieType) => ({
                    enabled:    true,
                    configHash: getCookieTypeConfigHash(cookieType),
                  }),
                )
                .toJS();

              return {
                cookieSettings_acceptAll:
                  () => dispatch(updateUserCookieSettings({cookieSettings: userSettings})),
                cookieSettings_displayCookieNotAllowedModal:
                  () => dispatch(displayCookieNotAllowedModal({cookieTypes: cookieSettings_requiredCookieTypes})),
              };
            },

            // Remove config props
            (
              stateProps,
              {cookieSettings_displayCookieNotAllowedModal, ...dispatchProps},
              ownProps,
            ) => {
              let props = Object.assign({}, ownProps, stateProps, dispatchProps);

              if (!stateProps.cookieSettings_areRequiredCookieTypesAllowed) {
                props = {
                  ...props,
                  cookieSettings_displayCookieNotAllowedModal,
                };
              }

              return props;
            },
          ),
        );

export default mapUserCookieSettings;



// WEBPACK FOOTER //
// ./src/components/profile/cookies/hocs/mapUserCookieSettings.js